import { useCallback, useEffect, useRef, useState } from 'react';
import { synthesizeSpeech, defaultVoiceFor, isLanguageSupported, loadTtsConfig } from '@/lib/kokoroTts';

interface SpeakButtonProps {
  /** Texte à lire à voix haute. */
  text: string;
  /** Langue du texte (fr, en, ar…). L'arabe retombe sur le français. */
  lang?: string;
  /** Voix Kokoro imposée (sinon voix par défaut de la langue). */
  voice?: string;
  /** Vitesse de lecture (0.5 → 2). */
  speed?: number;
  className?: string;
  /** Libellé affiché à côté de l'icône (masqué si `compact`). */
  label?: string;
  compact?: boolean;
  /** Style visuel : « light » (fond clair) ou « ghost » (sans fond). */
  tone?: 'light' | 'ghost' | 'solid';
}

/**
 * Bouton « Écouter » — lit n'importe quelle réponse de l'assistant avec la
 * synthèse vocale Kokoro. Un second clic met la lecture en pause.
 */
export default function SpeakButton({
  text,
  lang = 'fr',
  voice,
  speed = 1,
  className = '',
  label = 'Écouter',
  compact = false,
  tone = 'ghost',
}: SpeakButtonProps) {
  const [state, setState] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  const supported = isLanguageSupported(lang);

  // Le SuperAdmin peut couper la lecture audio pour toute la plateforme.
  // La dictée vocale (entrée) reste indépendante et continue de fonctionner.
  useEffect(() => {
    let cancelled = false;
    loadTtsConfig()
      .then((cfg) => { if (!cancelled) setEnabled(cfg.enabled); })
      .catch(() => { if (!cancelled) setEnabled(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => {
    audioRef.current?.pause();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const play = useCallback(async () => {
    if (state === 'playing') {
      audioRef.current?.pause();
      setState('idle');
      return;
    }
    if (state === 'loading') return;

    setError(null);
    try {
      setState('loading');
      if (!urlRef.current) {
        const { url } = await synthesizeSpeech({
          text,
          voice: voice || defaultVoiceFor(lang),
          speed,
        });
        urlRef.current = url;
      }
      let audio = audioRef.current;
      if (!audio) {
        audio = new Audio();
        audioRef.current = audio;
      }
      if (audio.src !== urlRef.current) audio.src = urlRef.current;
      audio.onended = () => setState('idle');
      await audio.play();
      setState('playing');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Lecture impossible.');
      setState('error');
      setTimeout(() => setState('idle'), 2500);
    }
  }, [state, text, voice, lang, speed]);

  if (enabled === false) return null;

  const base =
    tone === 'solid'
      ? 'bg-primary-500 text-background-50 hover:bg-primary-600 border-transparent'
      : tone === 'light'
        ? 'bg-background-50 text-foreground-600 border border-background-200/70 hover:text-primary-700 hover:border-primary-300'
        : 'text-foreground-400 hover:text-primary-600 hover:bg-primary-50 border-transparent';

  return (
    <button
      type="button"
      onClick={play}
      disabled={!text.trim()}
      title={
        supported
          ? state === 'playing'
            ? 'Mettre en pause'
            : 'Écouter cette réponse à voix haute'
          : 'Cette langue n’est pas encore couverte par la synthèse vocale'
      }
      className={`inline-flex items-center gap-1.5 rounded-full transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap ${base} ${
        compact ? 'w-7 h-7 justify-center' : 'px-2.5 h-7 text-[11px] font-medium'
      } ${className}`}
      aria-label="Écouter la réponse"
    >
      {state === 'loading' ? (
        <i className="ri-loader-4-line animate-spin text-sm"></i>
      ) : state === 'playing' ? (
        <i className="ri-pause-circle-fill text-sm text-primary-600"></i>
      ) : state === 'error' ? (
        <i className="ri-error-warning-line text-sm text-red-500"></i>
      ) : (
        <i className="ri-volume-up-line text-sm"></i>
      )}
      {!compact && <span>{state === 'error' ? (error ? 'Erreur' : 'Réessayer') : label}</span>}
    </button>
  );
}